import { useEffect, useState } from 'react';
import api from '@/services/api';
import toast from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import { AvatarWrapper } from '@/components/ui/avatar';
import { DashboardContent } from '@/layouts/dashboard';
import BtnGroup from '@/components/custom/matter-btn/BtnGroup';
import { Badge } from '@/components/ui/badge';
import { DiscursiveQuestionCard } from './discursive-question-card';
import { FiChevronsRight } from 'react-icons/fi';
import { Button } from '@/components/ui/button';
import { FilterSelectNative } from '@/components/custom/filter';
import { t } from 'i18next';
import { Loader } from '@/components/custom/loader';

export function ExamDiscursiveCorrection(props) {
  const { uri, questionBookId, studentId, attemptId } = props;
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [loadingAttempts, setLoadingAttempts] = useState(false);
  const [studentAttempts, setStudentAttempts] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [questionBook, setQuestionBook] = useState(null);
  const navigate = useNavigate();

  const getStatusBadge = status => {
    switch (status) {
      case 'waiting_correction':
        return { label: 'Aguardando correção', variant: 'warning' };
      case 'reviews':
        return { label: 'Em correção', variant: 'warning' };
      case 'approved':
        return { label: 'Aprovado', variant: 'success' };
      case 'disapproved':
        return { label: 'Reprovado', variant: 'danger' };
      case 'accepting_answers':
        return { label: 'Aceitando respostas', variant: 'neutral' };
      case 'review':
        return { label: 'Em correção', variant: 'warning' };
      default:
        return { label: '--', variant: 'neutral' };
    }
  };

  const startCorrection = async () => {
    const { data, error } = await api.post(`/question_books_users/${attemptId}/start_correction`);
    if (error) {
      toast.error('Erro ao iniciar correção');
    }
  };

  const getAttempts = async () => {
    setLoadingAttempts(true);
    const { data, error } = await api.get(`/question_books_users/attempts?question_book_id=${questionBookId}&user_id=${studentId}`);
    if (error) {
      toast.error('Erro ao buscar tentativas do aluno');
    } else {
      setStudentAttempts(data);
    }
    setLoadingAttempts(false);
  };

  const getQuestions = async () => {
    if (!selectedSubmission) return;
    setLoadingQuestions(true);
    const { data, error } = await api.get(`/question_books_users/correction_answers?question_book_id=${questionBookId}&user_id=${studentId}&attempt=${selectedSubmission?.attempt}`);
    if (error) {
      toast.error('Erro ao buscar tentativas do aluno');
    } else {
      setQuestions(data?.attributes.questions);
    }
    setLoadingQuestions(false);
  };

  const getQuestionBook = async () => {
    if (!selectedSubmission) return;
    setLoadingQuestions(true);
    const { data, error } = await api.get(`/question_books/${questionBookId}`);
    if (error) {
      toast.error('Erro ao buscar dados da prova');
    } else {
      setQuestionBook(data?.attributes);
    }
    setLoadingQuestions(false);
  };

  const isMock = questionBook?.visibility === 'open';
  const resultRoute = isMock ? `/resultados-simulado/${questionBookId}` : `/resultados-prova/${questionBookId}`;

  const breadcrumbs = {
    title: 'Ferramentas de Avaliação',
    nav: [
      {
        route: isMock ? '/avaliacoes/simulados' : '/avaliacoes/provas',
        name: isMock ? 'Simulados' : 'Provas',
        isActive: false
      },
      {
        route: resultRoute,
        name: questionBook?.title || 'Correção da prova',
        isActive: false
      },
      {
        route: uri,
        name: `Correção de questão dissertativa`,
        isActive: true
      }
    ]
  };

  useEffect(() => {
    getAttempts();
    getQuestionBook();
  }, []);

  useEffect(() => {
    if (studentAttempts?.length > 0) {
      setSelectedSubmission(studentAttempts?.find(item => item.id === attemptId)?.attributes);
    }
  }, [attemptId, studentAttempts]);

  useEffect(() => {
    if (selectedSubmission) {
      getQuestions();

      if (selectedSubmission?.status === 'waiting_correction') {
        startCorrection();
      }
    }
  }, [selectedSubmission]);

  const handleUpdateQuestion = updatedQuestion => {
    setQuestions(prev => {
      const updatedQuestions = prev.map(question => (question.id === updatedQuestion.id ? updatedQuestion : question));

      const discursiveQuestions = updatedQuestions.filter(q => q.modality === 'discursive');

      const allCorrected = discursiveQuestions.every(q => q?.answer?.['corrected-at']);

      if (allCorrected) {
        getAttempts();
      }

      return updatedQuestions;
    });
  };

  const discursiveQuestions = questions?.length > 0 ? questions?.filter(q => q.modality === 'discursive') : [];

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      {loadingAttempts && <Loader />}

      {!loadingAttempts && studentAttempts?.length > 0 && (
        <>
          <div className="mb-4">
            <FilterSelectNative
              label={t('exams.attempt')}
              value={selectedSubmission?.id}
              onChange={option => {
                navigate(`/correcao-discursiva/${questionBookId}/${studentId}/${option.target.value}`);
                setSelectedSubmission(studentAttempts?.find(item => item.id === option.target.value)?.attributes);
              }}
              options={studentAttempts?.map((attempt, i) => {
                return { title: `${i + 1}ª tentativa`, id: attempt.id };
              })}
              blankDisabled
            />
          </div>
          <div className="mb-3 flex items-center justify-between">
            <div className="round-dg__user">
              <Link
                to={`/usuario/metricas/${selectedSubmission?.user?.id}`}
                state={{ isRedirected: true }}
              >
                <AvatarWrapper
                  src={''}
                  text={selectedSubmission?.user?.name}
                  className="size-9"
                />
              </Link>

              <div className="mr-6 flex flex-col">
                <h3
                  className="round-dg__title font-bold text-primary-dark"
                  title={selectedSubmission?.user?.name}
                >
                  <Link
                    to={`/usuario/metricas/${selectedSubmission?.user?.id}`}
                    state={{ isRedirected: true }}
                  >
                    {selectedSubmission?.user?.name}
                  </Link>
                </h3>
                <span className="text-sm text-neutral">Nota: {selectedSubmission?.score || '--'}</span>
              </div>
            </div>
            <Badge
              variant={getStatusBadge(selectedSubmission?.status)?.variant}
              size="xs"
            >
              {getStatusBadge(selectedSubmission?.status)?.label}
            </Badge>
          </div>

          {loadingQuestions ? (
            <Loader />
          ) : (
            <>
              {discursiveQuestions?.map(question => (
                <DiscursiveQuestionCard
                  question={question}
                  onUpdateQuestion={handleUpdateQuestion}
                />
              ))}
            </>
          )}

          <div className="mt-4 flex justify-end">
            <Button
              variant="outline"
              type="button"
              onClick={() => navigate(resultRoute)}
            >
              Voltar para listagem de resultados
            </Button>
          </div>
        </>
      )}
    </DashboardContent>
  );
}
